import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as proposalsService from '../services/proposalsService';
import api from '../../../services/api';
import { ITEMS_PER_PAGE, statusToStageCode, isProposalExpired } from '../constants/proposalUtils';
import { useRealTimeChat } from '../../chat/context/RealTimeChatContext';
import { extractFilenameFromHeaders } from '../../rfp/shared/utils/documentProgress';

export const useProposals = (globalSearch, user) => {
    const navigate = useNavigate();
    const [rfpData, setRfpData] = useState([]);
    const [rfpStats, setRfpStats] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showExport, setShowExport] = useState(false);
    const [selectedRfpId, setSelectedRfpId] = useState(null);
    const [activeMenuId, setActiveMenuId] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [successToast, setSuccessToast] = useState(null);
    const [isFetchingFullData, setIsFetchingFullData] = useState(false);
    const [fetchingRfpTitle, setFetchingRfpTitle] = useState('');
    const [isBulkDownloading, setIsBulkDownloading] = useState(false);
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [isFilterChanging, setIsFilterChanging] = useState(false);

    // Server-side pagination metadata
    const [metadata, setMetadata] = useState({
        total_count: 0,
        returned: 0,
        skip: 0,
        limit: ITEMS_PER_PAGE
    });

    const userDesignation = (user?.designation || '').toLowerCase();
    const userRole = (user?.role || '').toLowerCase();
    const isManager = userDesignation.includes('lead') ||
        userDesignation.includes('manager') ||
        userDesignation.includes('admin') ||
        userRole.includes('lead') ||
        userRole.includes('manager') ||
        userRole.includes('admin');

    const isAdmin = userDesignation.includes('admin') || userRole.includes('admin');

    const menuRef = useRef(null);
    const moreFiltersRef = useRef(null);

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            const finalQuery = (searchQuery || globalSearch || '').trim();
            setDebouncedSearchQuery(finalQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery, globalSearch]);

    // Click outside handler for dropdowns
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (activeMenuId && menuRef.current && !menuRef.current.contains(e.target)) {
                setActiveMenuId(null);
            }
            if (showMoreFilters && moreFiltersRef.current && !moreFiltersRef.current.contains(e.target)) {
                setShowMoreFilters(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeMenuId, showMoreFilters]);

    const fetchProposalsAndMetrics = useCallback(async () => {
        const isSubsequentFetch = !loading;
        if (isSubsequentFetch) {
            setIsFilterChanging(true);
        } else {
            setLoading(true);
        }
        try {
            const apiSearch = debouncedSearchQuery;

            if (isManager) {
                // Team Lead Scoped API with Pagination
                const params = {
                    skip: (currentPage - 1) * ITEMS_PER_PAGE,
                    limit: ITEMS_PER_PAGE
                };

                const stageCode = statusToStageCode[statusFilter];
                if (stageCode) params.stage_code = stageCode;
                if (apiSearch) params.search = apiSearch;

                // fetchProposals points to /rfp/{lead|admin}/scoped/v1/rfps
                // fetchProposalMetrics points to /rfp/{lead|admin}/scoped/v1/summary
                let responseData;
                let summaryData;
                try {
                    [responseData, summaryData] = await Promise.all([
                        proposalsService.fetchProposals(params, isAdmin),
                        proposalsService.fetchProposalMetrics(isAdmin)
                    ]);
                } catch (err) {
                    // Fallback: if BE doesn't yet support stage_code (e.g. stage_code 10), query without stage_code and filter on FE
                    if (stageCode) {
                        const fallbackParams = { ...params };
                        delete fallbackParams.stage_code;
                        [responseData, summaryData] = await Promise.all([
                            proposalsService.fetchProposals(fallbackParams, isAdmin),
                            proposalsService.fetchProposalMetrics(isAdmin)
                        ]);
                    } else {
                        throw err;
                    }
                }

                let rfpsList = responseData.rfps || [];
                let totalCount = responseData.total_count || 0;

                // Client-side filtering fallback for expired filter if BE returned unfiltered proposals
                if (statusFilter === 'expired') {
                    const hasNonExpired = rfpsList.some(r => !isProposalExpired(r));
                    if (hasNonExpired) {
                        rfpsList = rfpsList.filter(r => isProposalExpired(r));
                        totalCount = rfpsList.length;
                    }
                }

                setRfpData(rfpsList);
                setMetadata({
                    total_count: totalCount,
                    returned: rfpsList.length,
                    skip: responseData.skip || 0,
                    limit: responseData.limit || ITEMS_PER_PAGE
                });

                // Use the new scoped summary API for metrics
                setMetrics(summaryData);

                // Map summary data to rfpStats for pill counts
                // If summaryData has the counts, we use them. 
                // Based on previous patterns, we assume these keys exist:
                setRfpStats({
                    all: { count: summaryData.total_active || 0 },
                    review: { count: summaryData.in_review || 0 },
                    submitted: { count: summaryData.submitted || 0 },
                    finalised: { count: summaryData.finalised || 0 },
                    accepted: { count: summaryData.accepted || 0 },
                    rejected: { count: summaryData.rejected || 0 },
                    go: { count: summaryData.go || 0 },
                    nogo: { count: summaryData.nogo || 0 },
                    pending: { count: summaryData.pending || 0 },
                    expired: { count: summaryData.expired || 0 },
                    due_this_week: { count: summaryData.due_this_week || 0 }
                });

            } else {

                // Legacy / General Fetch for non-managers
                const params = {};
                const apiStatus = (statusFilter === 'all' || statusFilter === 'total') ? '' : statusFilter;
                if (apiStatus) params.status = apiStatus;
                if (apiSearch) params.search = apiSearch;

                const list = await proposalsService.fetchEmployeeProposals(params);
                setRfpData(list);

                try {
                    const metricsData = await proposalsService.fetchProposalMetrics(isAdmin);
                    setMetrics(metricsData);
                } catch (err) {
                    console.error('Failed to fetch proposal metrics:', err);
                }
            }



        } finally {
            setLoading(false);
            if (isSubsequentFetch) {
                setTimeout(() => {
                    setIsFilterChanging(false);
                }, 300);
            }
        }
    }, [statusFilter, debouncedSearchQuery, currentPage, isManager, isAdmin, loading]);

    useEffect(() => {
        fetchProposalsAndMetrics();
    }, [fetchProposalsAndMetrics]);

    const { lastMessage } = useRealTimeChat();

    // Listen for WebSocket events to trigger real-time list updates
    useEffect(() => {
        if (!lastMessage) return;

        const { type } = lastMessage;
        
        const DASHBOARD_SYNC_EVENTS = [
            'RFP_ASSIGNED',
            'NOTIFICATION_RECEIVED',
            'SECTION_SUBMITTED',
            'SECTION_UPDATED',
            'SECTION_RETURNED',
            'SECTION_APPROVED',
            'SECTION_REASSIGNED'
        ];

        // Refresh the entire list if any target real-time state change occurs
        if (DASHBOARD_SYNC_EVENTS.includes(type)) {
            console.log(`✅ [WS REFRESH] Triggering dashboard sync due to: ${type}`);
            fetchProposalsAndMetrics();
        }
    }, [lastMessage, fetchProposalsAndMetrics]);

    const handleToggleSelect = (id) => {
        if (!id && id !== 0) return;
        setSelectedIds(prev => {
            const isIncluded = prev.some(item => String(item) === String(id));
            return isIncluded ? prev.filter(item => String(item) !== String(id)) : [...prev, id];
        });
    };

    const handleActionClick = async (e, action, prop) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        const id = prop._id || prop.rfp_id || prop.id;
        setActiveMenuId(null);

        switch (action) {
            case 'view': {
                const rfpTitle = prop.proposal_title || prop.extraction_data?.proposal_title || prop.filename || 'Proposal';
                setIsFetchingFullData(true);
                setFetchingRfpTitle(rfpTitle);
                try {
                    const res = await proposalsService.fetchFullProposalData(id);
                    const fullData = res.status === 200 ? res.data : prop;
                    const firstSecId = (fullData.sections || [])[0]?.id;
                    const targetUrl = firstSecId
                        ? `/proposals/analysis-result/${id}/secid/${firstSecId}`
                        : `/proposals/analysis-result/${id}`;

                    navigate(targetUrl, {
                        state: {
                            rfpId: id,
                            analysisData: fullData,
                            query_deadline: prop.query_deadline || prop.extraction_data?.query_deadline || '',
                            proposal_deadline: prop.proposal_deadline || prop.extraction_data?.submission_deadline || '',
                            valuation: prop.valuation || prop.extraction_data?.valuation || '',
                            fromProposals: true
                        }
                    });
                } catch {
                    navigate(`/proposals/analysis-result/${id}`, {
                        state: { rfpId: id, analysisData: prop, fromProposals: true }
                    });
                } finally {
                    setIsFetchingFullData(false);
                }
                break;
            }
            case 'duplicate':
                setRfpData(prev => [{
                    ...prop,
                    _id: `copy_${Date.now()}`,
                    filename: `${prop.filename || 'Untitled'} (Copy)`,
                    created_at: new Date().toISOString()
                }, ...prev]);
                break;
            case 'export':
                if (id) {
                    setSelectedRfpId(id);
                    setShowExport(true);
                }
                break;
            case 'delete':
                setItemToDelete(prop);
                break;
            default:
                break;
        }
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            if (itemToDelete.multiple) {
                const response = await proposalsService.deleteProposalsBulk(selectedIds);
                if (response.status === 200) {
                    setRfpData(prev => prev.filter(r => !selectedIds.includes(r._id || r.rfp_id || r.id)));
                    setSelectedIds([]);
                    setSuccessToast({ message: 'Proposals deleted successfully', rfpName: `${selectedIds.length} items` });
                    setTimeout(() => setSuccessToast(null), 6000);
                    fetchProposalsAndMetrics();
                }
            } else {
                const id = itemToDelete._id || itemToDelete.rfp_id || itemToDelete.id;
                const response = await proposalsService.deleteProposal(id);
                if (response.status === 200) {
                    setRfpData(prev => prev.filter(r => (r._id || r.rfp_id || r.id) !== id));
                    setSuccessToast({ message: response.data?.message || 'RFP deleted successfully', rfpName: itemToDelete.proposal_title || itemToDelete.filename });
                    setTimeout(() => setSuccessToast(null), 6000);
                    fetchProposalsAndMetrics();
                }
            }
        } catch (err) {
            setError('Network error while deleting RFP.');
        } finally {
            setIsDeleting(false);
            setItemToDelete(null);
        }
    };

    const handleBulkDownload = async () => {
        if (selectedIds.length === 0) {
            setSuccessToast({
                message: 'Selection Required',
                rfpName: 'Please select at least one proposal for bulk download.',
                type: 'warning'
            });
            setTimeout(() => setSuccessToast(null), 6000);
            return;
        }
        setIsBulkDownloading(true);
        try {
            const response = await proposalsService.downloadProposalsBulk({
                rfp_ids: selectedIds,
                format: 'pdf',
                language: 'en',
                theme: 'default',
                template_id: 'default'
            });

            // Extract filename from Content-Disposition header if provided by backend
            let filename = `rfp_reports_bulk_${new Date().toISOString().split('T')[0]}.zip`;
            const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
            if (disposition) {
                const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i);
                if (match && match[1]) {
                    filename = decodeURIComponent(match[1].trim());
                }
            }

            const contentType = response.headers?.['content-type'] || 'application/zip';
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setSuccessToast({
                message: 'Download Started',
                rfpName: `Successfully generated reports package for ${selectedIds.length} proposal${selectedIds.length > 1 ? 's' : ''}.`,
                type: 'success'
            });
            setTimeout(() => setSuccessToast(null), 5000);
        } catch (err) {
            console.error('Bulk download error:', err);
            let errorMsg = err.message || 'Download failed';
            let isBulkNotFound = err.response?.status === 404;

            if (err.response?.data instanceof Blob) {
                try {
                    const text = await err.response.data.text();
                    const parsed = JSON.parse(text);
                    errorMsg = parsed.detail || parsed.message || errorMsg;
                    if (typeof errorMsg === 'string' && (errorMsg.includes('bulk-download') || errorMsg.includes('not found'))) {
                        isBulkNotFound = true;
                    }
                } catch {
                    // response is not JSON
                }
            }

            // Graceful fallback: If backend bulk endpoint is not found, download reports directly
            if (isBulkNotFound) {
                try {
                    setSuccessToast({
                        message: 'Preparing Reports',
                        rfpName: `Downloading ${selectedIds.length} report(s) directly as PDF...`,
                        type: 'info'
                    });

                    for (const rfpId of selectedIds) {
                        try {
                            const res = await api.get(`/rfp/report/${rfpId}?format=pdf&language=en`, {
                                responseType: 'blob',
                                headers: { 'Accept-Language': 'en', 'language': 'en' }
                            });
                            const blob = new Blob([res.data], { type: 'application/pdf' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.style.display = 'none';
                            a.href = url;
                            a.download = extractFilenameFromHeaders(res, `RFP_Report_${rfpId}.pdf`);
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                            await new Promise(resolve => setTimeout(resolve, 350));
                        } catch (singleErr) {
                            console.error(`Failed to download report for ${rfpId}:`, singleErr);
                        }
                    }

                    setSuccessToast({
                        message: 'Download Completed',
                        rfpName: `Successfully downloaded PDF reports for ${selectedIds.length} proposal${selectedIds.length > 1 ? 's' : ''}.`,
                        type: 'success'
                    });
                    setTimeout(() => setSuccessToast(null), 5000);
                    return;
                } catch (fallbackErr) {
                    console.error('Fallback download error:', fallbackErr);
                }
            }

            setError(`Bulk Download Error: ${errorMsg}`);
        } finally {
            setIsBulkDownloading(false);
        }
    };

    const filteredResults = isManager ? rfpData : (Array.isArray(rfpData) ? rfpData : []).filter(rfp => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'expired') return isProposalExpired(rfp);
        if (rfpStats && rfpStats[statusFilter]?.rfp_ids) {
            const allowedIds = rfpStats[statusFilter].rfp_ids;
            const rfpId = rfp._id || rfp.rfp_id || rfp.id;
            return allowedIds.includes(String(rfpId));
        }
        return true;
    }).sort((a, b) => {
        const dateA = new Date(a.created_at || a.uploaded_at || 0);
        const dateB = new Date(b.created_at || b.uploaded_at || 0);
        return dateB.getTime() - dateA.getTime();
    });

    const totalPages = isManager
        ? Math.ceil(metadata.total_count / metadata.limit)
        : Math.ceil(filteredResults.length / ITEMS_PER_PAGE);

    const paginatedData = isManager
        ? rfpData
        : filteredResults.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const isNextDisabled = isManager
        ? (metadata.skip + metadata.returned >= metadata.total_count)
        : (currentPage === totalPages);

    return {
        rfpData, rfpStats, metrics, loading, error, setError,
        searchQuery, setSearchQuery, statusFilter, setStatusFilter,
        currentPage, setCurrentPage, selectedIds, setSelectedIds,
        showExport, setShowExport, selectedRfpId,
        activeMenuId, setActiveMenuId, itemToDelete, setItemToDelete,
        isDeleting, successToast, isFetchingFullData, fetchingRfpTitle,
        isBulkDownloading, showMoreFilters, setShowMoreFilters,
        menuRef, moreFiltersRef, handleToggleSelect, handleActionClick,
        confirmDelete, handleBulkDownload, fetchProposalsAndMetrics,
        filteredResults, totalPages, paginatedData, handlePageChange,
        metadata, isManager, isNextDisabled, isFilterChanging
    };
};

